import type { Chat } from "@/lib/db/schema";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from "./ui/sidebar";
import Link from "next/link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  CheckCircleFillIcon,
  GlobeIcon,
  LockIcon,
  MoreHorizontalIcon,
  PencilEditIcon,
  ShareIcon,
  TrashIcon,
} from "./icons";
import { Star, GitFork } from "lucide-react";
import { memo, useState, useRef, useEffect } from "react";
import { useChatVisibility } from "@/hooks/use-chat-visibility";
import type { SessionActivity } from "@/hooks/use-active-sessions";
import { telemetry } from "@/lib/telemetry";

const PureChatItem = ({
  chat,
  isActive,
  sessionActivity = "idle",
  onDelete,
  onRename,
  onFavorite,
  onFork,
  setOpenMobile,
}: {
  chat: Chat;
  isActive: boolean;
  sessionActivity?: SessionActivity;
  onDelete: (chatId: string) => void;
  onRename?: (chatId: string, newTitle: string) => void;
  onFavorite?: (chatId: string) => void;
  onFork?: (chatId: string) => void;
  setOpenMobile: (open: boolean) => void;
}) => {
  const { visibilityType, setVisibilityType } = useChatVisibility({
    chatId: chat.id,
    initialVisibilityType: chat.visibility,
  });

  // Simple state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [displayTitle, setDisplayTitle] = useState(chat.title || "");
  const inputRef = useRef<HTMLInputElement>(null);

  // Simple auto-focus
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Simple functions
  const startEditing = () => {
    setEditValue(chat.title || "");
    setIsEditing(true);
  };

  const saveEditing = () => {
    const newTitle = editValue.trim();
    setDisplayTitle(newTitle); // Instant UI update
    setIsEditing(false);
    if (newTitle !== chat.title && onRename) {
      onRename(chat.id, newTitle);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") saveEditing();
    if (e.key === "Escape") setIsEditing(false);
  };

  const activityRingClass =
    sessionActivity === "running"
      ? "sidebar-running"
      : sessionActivity === "recently_finished"
        ? "sidebar-finished"
        : "";

  return (
    <SidebarMenuItem className={activityRingClass}>
      <SidebarMenuButton asChild isActive={isActive}>
        <Link
          href={`/chat/${chat.id}`}
          onClick={() => {
            telemetry.emit("session.open", {
              session_id: chat.id,
              user_id: chat.userId,
              source: "sidebar_history",
              is_active: isActive,
              activity: sessionActivity,
            });
            setOpenMobile(false);
          }}
        >
          {isEditing ? (
            <input
              ref={inputRef}
              value={editValue}
              onChange={(e) => {
                setEditValue(e.target.value);
                setDisplayTitle(e.target.value); // Instant frontend update
              }}
              onBlur={saveEditing}
              onKeyDown={handleKeyDown}
              className={`bg-transparent border-none outline-none w-full ${
                isActive
                  ? "text-[hsl(var(--sidebar-session-title-active))]"
                  : "text-[hsl(var(--sidebar-session-title))]"
              }`}
              onClick={(e) => e.preventDefault()}
            />
          ) : (
            <span
              className={
                isActive
                  ? "text-[hsl(var(--sidebar-session-title-active))]"
                  : "text-[hsl(var(--sidebar-session-title))]"
              }
            >
              {displayTitle}
            </span>
          )}
        </Link>
      </SidebarMenuButton>

      <DropdownMenu modal={true}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground mr-0.5"
            showOnHover={!isActive}
          >
            <MoreHorizontalIcon />
            <span className="sr-only">More</span>
          </SidebarMenuAction>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="cursor-pointer">
              <ShareIcon />
              <span>Share</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  className="cursor-pointer flex-row justify-between"
                  onClick={() => {
                    setVisibilityType("private");
                  }}
                >
                  <div className="flex flex-row gap-2 items-center">
                    <LockIcon size={12} />
                    <span>Private</span>
                  </div>
                  {visibilityType === "private" ? (
                    <CheckCircleFillIcon />
                  ) : null}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer flex-row justify-between"
                  onClick={() => {
                    setVisibilityType("public");
                  }}
                >
                  <div className="flex flex-row gap-2 items-center">
                    <GlobeIcon />
                    <span>Public</span>
                  </div>
                  {visibilityType === "public" ? <CheckCircleFillIcon /> : null}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => {
              telemetry.emit("session.rename_click", {
                session_id: chat.id,
                user_id: chat.userId,
                source: "sidebar_menu",
              });
              setTimeout(startEditing, 250);
            }}
          >
            <PencilEditIcon />
            <span>Rename</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={() => onFork?.(chat.id)}
          >
            <GitFork className="h-4 w-4" />
            <span>Fork chat</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => onFavorite?.(chat.id)}
          >
            <Star className={`h-4 w-4 ${(chat as any).is_favorited ? "fill-[#fbbf24] text-[#fbbf24]" : ""}`} />
            <span>{(chat as any).is_favorited ? "Unfavorite" : "Favorite"}</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer text-destructive focus:bg-destructive/15 focus:text-destructive dark:text-red-500"
            onSelect={() => onDelete(chat.id)}
          >
            <TrashIcon />
            <span>Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};

export const ChatItem = memo(PureChatItem, (prevProps, nextProps) => {
  if (prevProps.isActive !== nextProps.isActive) return false;
  if (prevProps.chat.id !== nextProps.chat.id) return false;
  if ((prevProps.chat as any).is_favorited !== (nextProps.chat as any).is_favorited) return false;
  if (prevProps.chat.title !== nextProps.chat.title) return false;
  if (prevProps.sessionActivity !== nextProps.sessionActivity) return false;
  return true;
});
